/**
 * VisualStyleSelector — Video News Studio Visual Style picker
 *
 * Drops a 3-card selector (👤 AI Avatar / 🖼 Topic Image / 〰 Waveform) plus
 * conditional avatar picker into any news-video flow. Used by:
 *   - NewsCenter.jsx (the "Approve & broadcast" block)
 *   - NewsStudio.jsx (individual bulletin builder)
 *
 * Controlled component: owner manages `value` and `onChange({visual_style,
 * avatar_id, avatar_source_url})`. Default `visual_style` is `avatar` (per
 * Update #81 — showcases the new feature; gracefully falls back to waveform
 * on D-ID failure).
 */
import React, { useEffect, useState } from "react";
import { User, Image as ImageIcon, AudioWaveform, Upload, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STYLE_CARDS = [
  {
    id: "avatar",
    label: "AI Avatar",
    icon: User,
    description: "AI presenter reads your script on camera",
    badge: "D-ID",
  },
  {
    id: "image",
    label: "Topic Image",
    icon: ImageIcon,
    description: "Cinematic image auto-matched to your story",
    badge: "Auto",
  },
  {
    id: "waveform",
    label: "Waveform",
    icon: AudioWaveform,
    description: "Classic audio visualizer with animated wave",
    badge: "Default",
  },
];

export default function VisualStyleSelector({
  value,            // {visual_style, avatar_id, avatar_source_url}
  onChange,
  compact = false,  // tighter spacing for inline use
  className = "",
  "data-testid": dataTestid = "visual-style-selector",
}) {
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [byoaUrl, setByoaUrl] = useState(value?.avatar_source_url || "");
  const visualStyle = value?.visual_style || "avatar";
  const avatarId = value?.avatar_id || "amy";

  // Fetch the D-ID preset avatars once
  useEffect(() => {
    let cancelled = false;
    setPresetsLoading(true);
    (async () => {
      try {
        const r = await api.get("/news/avatars");
        if (!cancelled) setPresets(r.data?.avatars || []);
      } catch {
        if (!cancelled) setPresets([]);
      } finally {
        if (!cancelled) setPresetsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (patch) => onChange?.({
    visual_style: visualStyle,
    avatar_id: avatarId,
    avatar_source_url: byoaUrl || undefined,
    ...patch,
  });

  const selectStyle = (id) => update({ visual_style: id });
  const selectPreset = (id) => {
    setByoaUrl("");
    update({ visual_style: "avatar", avatar_id: id, avatar_source_url: undefined });
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Avatar must be < 10MB");
      return;
    }
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/news/avatars/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.source_url || "";
      if (!url) throw new Error("upload returned no source_url");
      setByoaUrl(url);
      update({ visual_style: "avatar", avatar_id: "byoa", avatar_source_url: url });
      toast.success("Custom avatar uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Avatar upload failed");
    } finally {
      setUploadBusy(false);
      // reset input so re-selecting the same file fires again
      try { e.target.value = ""; } catch { /* noop */ }
    }
  };

  const pad = compact ? "p-3" : "p-4";
  const gap = compact ? "gap-2" : "gap-3";

  return (
    <div className={`rounded-xl border border-line bg-bg-surface ${pad} ${className}`} data-testid={dataTestid}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">// VISUAL STYLE</div>
        <div className="text-[10px] text-ink-muted">
          {visualStyle === "avatar"
            ? "Talking-head presenter (D-ID, ~30s render)"
            : visualStyle === "image"
            ? "Auto-matched cinematic image (Ken Burns)"
            : "Animated audio waveform (instant)"}
        </div>
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-3 ${gap}`}>
        {STYLE_CARDS.map((c) => {
          const Icon = c.icon;
          const selected = visualStyle === c.id;
          return (
            <button
              key={c.id}
              type="button"
              data-testid={`visual-style-${c.id}`}
              onClick={() => selectStyle(c.id)}
              className={`text-left rounded-lg transition-all relative overflow-hidden ${pad} ${
                selected
                  ? "border-2 border-brand-blue bg-brand-blue/10"
                  : "border border-line bg-bg-elevated hover:border-brand-blue/40"
              }`}
              style={!selected ? { borderColor: "#1e2130" } : undefined}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div
                  className={`h-8 w-8 rounded-md grid place-items-center ${
                    selected ? "bg-brand-blue/20" : "bg-bg"
                  }`}
                >
                  <Icon className={`h-4 w-4 ${selected ? "text-brand-blue" : "text-ink-muted"}`} />
                </div>
                {selected && <Check className="h-4 w-4 text-brand-blue shrink-0" />}
              </div>
              <div className={`text-sm font-semibold ${selected ? "text-ink-primary" : "text-ink-primary"}`}>
                {c.label}
              </div>
              <div className="text-[11px] text-ink-muted mt-0.5 leading-snug">{c.description}</div>
              <div className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono ${
                selected ? "bg-brand-blue/20 text-brand-blue" : "bg-bg text-ink-muted"
              }`}>
                {c.badge}
              </div>
            </button>
          );
        })}
      </div>

      {/* Avatar picker — shown only when "avatar" is selected */}
      {visualStyle === "avatar" && (
        <div className="mt-4 rounded-md border border-line bg-bg-elevated p-3" data-testid="avatar-picker">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
            // PRESENTER
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            {presetsLoading && (
              <div className="text-[11px] text-ink-muted inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> loading presenters…
              </div>
            )}
            {presets.map((a) => {
              const isPick = avatarId === a.id && !byoaUrl;
              return (
                <button
                  key={a.id}
                  type="button"
                  data-testid={`avatar-preset-${a.id}`}
                  onClick={() => selectPreset(a.id)}
                  className="flex flex-col items-center gap-1.5 group"
                  title={a.tag}
                >
                  <div
                    className={`h-14 w-14 rounded-full overflow-hidden bg-bg ring-offset-2 ring-offset-bg-elevated transition-all ${
                      isPick ? "ring-2 ring-brand-blue scale-105" : "ring-1 ring-line opacity-80 group-hover:opacity-100"
                    }`}
                  >
                    <img
                      src={a.source_url}
                      alt={a.label}
                      className="h-full w-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        // fallback portrait if image_url is unreachable
                        e.currentTarget.style.background = "#1e2130";
                      }}
                    />
                  </div>
                  <div className="text-[10px] text-ink-muted leading-tight">{a.label}</div>
                </button>
              );
            })}
            {/* BYOA upload tile */}
            <label
              className="flex flex-col items-center gap-1.5 group cursor-pointer"
              data-testid="avatar-byoa"
            >
              <div
                className={`h-14 w-14 rounded-full border-2 border-dashed grid place-items-center transition-all overflow-hidden ${
                  byoaUrl
                    ? "border-brand-blue bg-brand-blue/10"
                    : "border-line text-ink-muted hover:border-brand-blue/40"
                }`}
              >
                {byoaUrl ? (
                  <img src={byoaUrl} alt="Your avatar" className="h-full w-full object-cover" />
                ) : uploadBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin text-brand-blue" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
              </div>
              <div className="text-[10px] text-ink-muted leading-tight">
                {byoaUrl ? "Yours" : uploadBusy ? "Uploading…" : "Upload"}
              </div>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={onFileChange}
                disabled={uploadBusy}
                data-testid="avatar-byoa-input"
              />
            </label>
          </div>
          <div className="mt-2 text-[10px] text-ink-muted leading-relaxed">
            Front-facing portrait, neutral expression, well-lit. PNG/JPG/WEBP up to 10MB. D-ID handles the lip-sync.
          </div>
        </div>
      )}
    </div>
  );
}
